import { createContext, type PropsWithChildren, useContext, useEffect, useMemo, useRef } from "react";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useCodeListsQuery } from "@/api/hooks/useCodeLists.ts";
import { useAuth } from "@/auth/useAuth.ts";
import type { components } from "@/api/schema.ts";
import { useMerchantConfigurationQuery } from "@/api/hooks/useMerchantConfiguration.ts";

export type ListsItem = Exclude<components["schemas"]["CodeList"]["items"], undefined>[number];

type ConfigData = {
  currencies: ListsItem[];
  apps: ListsItem[];
  showManagedAccounts: boolean;
  activeApp: string;
  setActiveApp: (app?: string) => void;
  isPending: boolean;
  isError: boolean;
};

const configDefault: ConfigData = {
  currencies: [],
  apps: [],
  showManagedAccounts: false,
  activeApp: "",
  setActiveApp: () => {},
  isPending: false,
  isError: false,
};

export function ConfigDataProvider({ children }: PropsWithChildren) {
  const { isAuthenticated } = useAuth();

  const {
    data,
    isPending: isCodesPending,
    isError: isCodesError,
  } = useCodeListsQuery({ codeListCodes: ["CURRENCY", "CUSTOMER_APPLICATION"] }, { enabled: isAuthenticated });

  const listData = useMemo(() => {
    if (!data) return;

    const apps: ListsItem[] = [];
    const currencies: ListsItem[] = [];

    for (const list of data) {
      if (list.code === "CUSTOMER_APPLICATION") apps.push(...(list.items ?? []));
      if (list.code === "CURRENCY") currencies.push(...(list.items ?? []));
    }

    return { apps, currencies };
  }, [data]);

  const [activeApp, setActiveApp] = useLocalStorage<string | undefined>("@fin_merch_active_app", undefined);
  const activeAppRef = useRef(activeApp);
  activeAppRef.current = activeApp;
  const setActiveAppRef = useRef(setActiveApp);
  setActiveAppRef.current = setActiveApp;

  useEffect(() => {
    if (!listData) return;

    if (!listData.apps.length) return setActiveAppRef.current(undefined);

    if (activeAppRef.current && listData.apps.some(({ code }) => code === activeAppRef.current)) return;

    setActiveAppRef.current(listData.apps[0].code);
  }, [listData]);

  const {
    data: configData,
    isPending: isConfigPending,
    isError: isConfigError,
  } = useMerchantConfigurationQuery(
    {
      merchantId: activeApp ?? "",
    },
    { enabled: !!activeApp },
  );

  const isPending = isConfigPending || isCodesPending;
  const isError = isConfigError || isCodesError;

  const value = useMemo(
    () => ({
      apps: listData ? listData?.apps : [],
      currencies: listData ? listData.currencies : [],
      showManagedAccounts: configData?.showManagedAccounts ?? false,
      activeApp: activeApp ? activeApp : "",
      setActiveApp: setActiveApp,
      isPending,
      isError,
    }),
    [activeApp, configData?.showManagedAccounts, isError, isPending, listData, setActiveApp],
  );

  if (!listData || !activeApp) return null;

  return <ConfigCtx.Provider value={value}>{children}</ConfigCtx.Provider>;
}

export const ConfigCtx = createContext<ConfigData>(configDefault);

export const useAppConfig = () => useContext(ConfigCtx);
